/**
 * src/components/shared/CardActions.jsx — Shared card interaction primitives
 * AC Nautitech Design System
 *
 * Provides Trello-style hover toolbar and right-click context menu for all card types.
 *
 * Exports:
 *   useCardActions()  — hook returning menuPos, onContextMenu, closeMenu
 *   HoverToolbar      — 4-button toolbar (Edit, Archive, Move, More), appears on desktop hover (top-right)
 *   ContextMenu       — right-click menu with configurable actions
 */

import { useState, useRef, useEffect } from 'react';
import { Edit3, Archive, ArrowRight, MoreHorizontal, Link } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useT } from '../../lib/i18n/I18nContext';

export function useCardActions() {
  const [menuPos, setMenuPos] = useState(null);
  const menuRef = useRef(null);

  const onContextMenu = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setMenuPos({ x: e.clientX, y: e.clientY });
  };

  const closeMenu = () => setMenuPos(null);

  // Close on click outside or Escape
  useEffect(() => {
    if (!menuPos) return;
    const handleClick = () => closeMenu();
    const handleKey = (e) => e.key === 'Escape' && closeMenu();
    document.addEventListener('click', handleClick);
    document.addEventListener('keydown', handleKey);
    return () => {
      document.removeEventListener('click', handleClick);
      document.removeEventListener('keydown', handleKey);
    };
  }, [menuPos]);

  return { menuPos, menuRef, onContextMenu, closeMenu };
}

// Hover toolbar — render inside a relative-positioned card container
export function HoverToolbar({ onEdit, onArchive, onMove, onMore, alwaysShowMore = false }) {
  const { t } = useT();
  const [touchMenuOpen, setTouchMenuOpen] = useState(false);
  const popoverRef = useRef(null);

  const isTouchDevice =
    typeof window !== 'undefined' &&
    window.matchMedia?.('(hover: none)').matches;

  const showTouchMode = isTouchDevice || alwaysShowMore;

  // Close popover on click outside
  useEffect(() => {
    if (!touchMenuOpen) return;
    const handleClick = (e) => {
      if (popoverRef.current && !popoverRef.current.contains(e.target)) {
        setTouchMenuOpen(false);
      }
    };
    const handleKey = (e) => e.key === 'Escape' && setTouchMenuOpen(false);
    document.addEventListener('click', handleClick);
    document.addEventListener('keydown', handleKey);
    return () => {
      document.removeEventListener('click', handleClick);
      document.removeEventListener('keydown', handleKey);
    };
  }, [touchMenuOpen]);

  if (showTouchMode) {
    // Build action list for the popover
    const actions = [
      onEdit    && { icon: Edit3,       label: t('common.edit'),           handler: onEdit },
      onArchive && { icon: Archive,     label: t('taskCard.archive'),      handler: onArchive },
      onMove    && { icon: ArrowRight,  label: t('taskCard.move'),         handler: onMove },
      onMore    && { icon: MoreHorizontal, label: t('taskCard.moreActions'), handler: onMore },
    ].filter(Boolean);

    return (
      <div ref={popoverRef} className="absolute top-1.5 left-1.5 z-10">
        {/* Persistent 3-dot button — positioned top-left to avoid blocking checkbox on right */}
        <button
          onClick={(e) => { e.stopPropagation(); setTouchMenuOpen((v) => !v); }}
          className="w-11 h-11 rounded-full flex items-center justify-center pointer-events-auto"
          style={{ background: 'rgba(255,255,255,0.80)', boxShadow: '0 1px 3px rgba(0,0,0,0.12)' }}
          aria-label={t('taskCard.moreActions')}
        >
          <MoreHorizontal size={16} strokeWidth={1.5} color="var(--t2)" />
        </button>

        {/* Touch popover */}
        <AnimatePresence>
          {touchMenuOpen && (
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: -4 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: -4 }}
              transition={{ duration: 0.12 }}
              style={{
                position: 'absolute',
                top: 36,
                left: 0,
                background: 'var(--card)',
                borderRadius: 10,
                boxShadow: '0 4px 16px rgba(0,0,0,0.12), 0 1px 4px rgba(0,0,0,0.08)',
                padding: 4,
                minWidth: 140,
                zIndex: 20,
              }}
              onClick={(e) => e.stopPropagation()}
            >
              {actions.map(({ icon: Icon, label, handler }) => (
                <button
                  key={label}
                  onClick={() => { handler(); setTouchMenuOpen(false); }}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                    width: '100%',
                    padding: '8px 12px',
                    textAlign: 'left',
                    fontSize: 13,
                    fontWeight: 500,
                    color: 'var(--navy)',
                    background: 'transparent',
                    border: 'none',
                    borderRadius: 7,
                    cursor: 'pointer',
                  }}
                  onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(0,0,0,0.04)'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
                >
                  <Icon size={14} strokeWidth={1.5} color="var(--tm)" />
                  {label}
                </button>
              ))}
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    );
  }

  // Desktop: existing hover toolbar — unchanged
  return (
    <div className="absolute top-1.5 right-1.5 opacity-0 group-hover:opacity-100 transition-opacity duration-150 flex gap-0.5 z-10 pointer-events-none">
      {onEdit && (
        <button onClick={(e) => { e.stopPropagation(); onEdit(); }}
          className="w-7 h-7 rounded-md flex items-center justify-center bg-[var(--card)] hover:bg-[var(--glass)] shadow-sm border border-[var(--bdr)] transition-colors pointer-events-auto"
          title={t('common.edit')}>
          <Edit3 size={13} strokeWidth={1.5} color="var(--t2)" />
        </button>
      )}
      {onArchive && (
        <button onClick={(e) => { e.stopPropagation(); onArchive(); }}
          className="w-7 h-7 rounded-md flex items-center justify-center bg-[var(--card)] hover:bg-[var(--glass)] shadow-sm border border-[var(--bdr)] transition-colors pointer-events-auto"
          title={t('taskCard.archive')}>
          <Archive size={13} strokeWidth={1.5} color="var(--t2)" />
        </button>
      )}
      {onMove && (
        <button onClick={(e) => { e.stopPropagation(); onMove(); }}
          className="w-7 h-7 rounded-md flex items-center justify-center bg-[var(--card)] hover:bg-[var(--glass)] shadow-sm border border-[var(--bdr)] transition-colors pointer-events-auto"
          title={t('taskCard.move')}>
          <ArrowRight size={13} strokeWidth={1.5} color="var(--t2)" />
        </button>
      )}
      {onMore && (
        <button onClick={(e) => { e.stopPropagation(); onMore(); }}
          className="w-7 h-7 rounded-md flex items-center justify-center bg-[var(--card)] hover:bg-[var(--glass)] shadow-sm border border-[var(--bdr)] transition-colors pointer-events-auto"
          title={t('taskCard.moreActions')}>
          <MoreHorizontal size={13} strokeWidth={1.5} color="var(--t2)" />
        </button>
      )}
    </div>
  );
}

// Context menu — render via portal or fixed position
export function ContextMenu({ pos, onClose, actions }) {
  if (!pos) return null;
  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0, scale: 0.95, y: -4 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.95 }}
        transition={{ duration: 0.12 }}
        className="fixed z-50 min-w-[180px] rounded-xl bg-[var(--card)] shadow-lg border border-[var(--bdr)] py-1.5 overflow-hidden"
        style={{ left: pos.x, top: pos.y }}
        onClick={(e) => e.stopPropagation()}
      >
        {actions.map((action, i) =>
          action.divider ? (
            <div key={i} className="h-px bg-[var(--bdr)] my-1" />
          ) : (
            <button key={i}
              onClick={() => { action.onClick(); onClose(); }}
              className="w-full px-3 py-2 text-left text-[13px] font-medium flex items-center gap-2.5 hover:bg-black/4 transition-colors"
              style={{ color: action.danger ? 'var(--bad)' : 'var(--navy)' }}
            >
              {action.icon && <action.icon size={14} strokeWidth={1.5} style={{ color: action.danger ? 'var(--bad)' : 'var(--tm)' }} />}
              {action.label}
            </button>
          )
        )}
      </motion.div>
    </AnimatePresence>
  );
}
